import {
  AuthenticationResult,
  IPublicClientApplication,
  SilentRequest,
  createStandardPublicClientApplication,
} from "@azure/msal-browser";
import posthog from "posthog-js";
import { MSAL_CONFIG } from "./config";
import { APPLICATION_INSIGHTS } from "./appinsights";

class Msal {
  private _client: IPublicClientApplication | undefined = undefined;
  private async client(): Promise<IPublicClientApplication> {
    if (this._client) {
      return this._client;
    }
    const client = await createStandardPublicClientApplication(MSAL_CONFIG);
    await client.initialize();
    return client;
  }
  private set authResult(result: AuthenticationResult | undefined) {
    if (result) {
      this._client?.setActiveAccount(result.account);
      APPLICATION_INSIGHTS.setAuthenticatedUserContext(result.account.username);
      posthog.identify(result.account.username);
    } else {
      this._client?.setActiveAccount(null);
      APPLICATION_INSIGHTS.clearAuthenticatedUserContext();
      posthog.reset();
    }
  }

  public async login(): Promise<AuthenticationResult> {
    try {
      if (!this._client) {
        this._client = await this.client();
      }
      const request: SilentRequest = { scopes: ["User.Read"] };
      const result = await this._client.loginPopup(request);
      if (!result.account) {
        throw new Error("Login failed: No account information returned.");
      }
      this.authResult = result;
      return result;
    } catch (error) {
      this.authResult = undefined;
      posthog.capture("msal_login_error", { error: error.message });
      APPLICATION_INSIGHTS.trackException({ exception: error });
      throw new Error(`MSAL Login Error: ${error.message}`);
    }
  }

  public async token(scopes: string[]): Promise<string> {
    try {
      if (!this._client) {
        this._client = await this.client();
      }
      const request: SilentRequest = {
        scopes: scopes,
        account: this._client.getActiveAccount() || undefined,
        prompt: "select_account",
      };
      try {
        const result = await this._client.acquireTokenSilent(request);
        this.authResult = result;
        return result.accessToken;
      } catch (error) {
        try {
          const result = await this._client.acquireTokenPopup(request);
          this.authResult = result;
          return result.accessToken;
        } catch (error) {
          this.authResult = undefined;
          posthog.capture("msal_token_error", { error: error, message: error.message });
          APPLICATION_INSIGHTS.trackException({ exception: error });
          throw new Error(`MSAL Token Error: ${error.message}`);
        }
      }
    } catch (error) {
      this.authResult = undefined;
      posthog.capture("msal_token_error", { error: error.message });
      APPLICATION_INSIGHTS.trackException({ exception: error });
      throw new Error(`MSAL Token Error: ${error.message}`);
    }
  }
}

export const MSAL = new Msal();
